import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { Link } from "react-router-dom";

const siteUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL
  ? import.meta.env.VITE_SITE_URL
  : 'https://cssspreadsheet.com';

const pageUrl = `${siteUrl}/articles/superbuy-beginner-guide`;

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Superbuy Spreadsheet Guide for Beginners 2026',
  description: 'New to Superbuy? This beginner-friendly guide walks you through setting up your first Superbuy spreadsheet, understanding the platform features, and placing your first order with complete confidence.',
  datePublished: '2026-07-06',
  dateModified: '2026-07-06',
  author: {
    '@type': 'Organization',
    name: 'CSSSpreadsheet',
  },
  publisher: {
    '@type': 'Organization',
    name: 'CSSSpreadsheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': pageUrl,
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Superbuy',
    },
  ],
};

export default function ArticlePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd schema={articleSchema} />
      <JsonLd schema={breadcrumbSchema} />
      <Navbar />

      <main className="pt-28 pb-16">
        <article className="mx-auto max-w-4xl px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors underline">Home</Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
            <span className="text-foreground-800">Superbuy</span>
          </nav>

          {/* Article Header */}
          <header className="mb-12">
            <span className="inline-block px-4 py-1.5 bg-accent-100/60 rounded-full text-xs font-semibold text-accent-800 mb-4 tracking-wider">
              BEGINNER FRIENDLY
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
              Superbuy Spreadsheet Guide for Beginners 2026
            </h1>
            <p className="text-foreground-600 text-base leading-relaxed mb-4">
              New to Superbuy? This beginner-friendly guide walks you through setting up your first <strong>Superbuy spreadsheet</strong>, understanding the platform features, and placing your first order with complete confidence.
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>Updated: July 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>7 min read</span>
            </div>
          </header>

          {/* Article Body */}
          <div className="prose-custom space-y-10 text-foreground-800 leading-relaxed">

            {/* Section 1 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                What Makes Superbuy Different for New Users
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Superbuy has long been known as one of the most beginner-friendly agent platforms, and their approach to spreadsheet integration reflects that philosophy. While the <strong>CSSBuy spreadsheet</strong> and <strong>Kakobuy spreadsheet</strong> ecosystems tend to attract more experienced users who want maximum customization, the <strong>Superbuy spreadsheet</strong> approach is designed to be approachable from day one.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The key difference is that Superbuy offers some built-in data export functionality that other platforms do not. This means you can partially automate the process of populating your <strong>Superbuy spreadsheet</strong> with order information, reducing the manual entry burden that often discourages beginners from adopting spreadsheet workflows in the first place.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                However, this convenience comes with tradeoffs. The built-in export tools provide a more structured but less flexible data format. If you are the type of user who wants to add custom fields, track niche metrics, or experiment with different organizational approaches, you may find the <strong>Superbuy spreadsheet</strong> system somewhat limiting compared to the more open-ended CSSBuy alternative. For a detailed comparison of the different platform approaches, check our <Link to="/articles/cssbuy-sheet-complete-guide" className="text-primary-600 underline hover:text-primary-700">complete CSSBuy sheet guide</Link>.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Setting Up Your Superbuy Account and First Spreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Getting started with Superbuy requires a straightforward registration process. Create your account using a valid email address, complete the basic verification steps, and familiarize yourself with the dashboard layout before placing your first order. The platform interface is clean and well-organized, which reduces the initial overwhelm that many beginners experience with other agents.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                For your first <strong>Superbuy spreadsheet</strong>, we recommend starting with a simple template rather than building from scratch. Create a basic spreadsheet with the following columns: order date, product name, item URL, Chinese size or specification, agent price, estimated shipping cost, order status, QC photo link, and final review notes. This structure captures the essential information without overwhelming you with complexity.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Superbuy's export feature can populate some of these columns automatically. Navigate to your order history, select the export option, and download the CSV file. Import this data into your spreadsheet as a starting point, then add your own custom columns for any additional tracking you want to maintain. This hybrid approach gives you the best of both worlds: platform automation plus personal customization.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Placing and Tracking Your First Superbuy Order
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Placing your first order through Superbuy is intentionally simple. Paste your product link into the purchase form, select your size and color options, review the price breakdown, and confirm the order. Superbuy provides clear pricing summaries that show the item cost, domestic shipping within China, service fee, and estimated international shipping.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Once your order is placed, the real power of your <strong>Superbuy spreadsheet</strong> becomes apparent. As your item moves through the workflow, update the status column in your spreadsheet. Common status values include Order Placed, Seller Processing, Shipped to Warehouse, Arrived at Warehouse, QC Photos Taken, Approved, Packed for Shipping, and Shipped Internationally.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Tracking multiple orders simultaneously is where the spreadsheet truly shines. Instead of checking each order individually in the Superbuy dashboard, your spreadsheet gives you a bird's-eye view of your entire purchasing pipeline. This visibility is particularly valuable when you have items at different stages of the process, allowing you to prioritize which QC photos to review first and which shipments to consolidate.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Free Superbuy Spreadsheet Template for Beginners
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                To help new users get started, we have developed a free <strong>Superbuy spreadsheet</strong> template that you can copy and customize to your needs. The template includes pre-configured dropdown lists for common status values, automated price calculations, and conditional formatting that highlights orders requiring attention.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The template is structured with a main tracking sheet for active orders, a completed orders archive, a seller directory for building your trusted source list, and a shipping cost reference table. Each sheet is designed to work together, creating a comprehensive purchase management system that grows with your experience level.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                As you gain confidence and identify additional tracking needs, feel free to modify the template. Add columns for return requests, dispute outcomes, seller communication notes, or any other data points that help you shop smarter. The goal is to build a system that serves your specific workflow, not to rigidly follow someone else's template.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Common Beginner Mistakes and How to Avoid Them
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Every beginner makes mistakes, and agent shopping is no exception. The most common error we see is placing too many simultaneous orders before understanding the platform workflow. Start with one or two items for your first haul. This gives you a low-stakes environment to learn the process without risking a large sum of money on unfamiliar procedures.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Another frequent mistake is neglecting to track sizing information carefully. Chinese sizing often differs from Western standards, and clothing measurements can vary significantly between sellers. Your <strong>Superbuy spreadsheet</strong> should include a dedicated sizing column where you record the size ordered, the corresponding measurement in centimeters, and notes about fit after receiving the item.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                Finally, many beginners fail to understand the difference between item cost and total cost. The total includes the product price, domestic shipping, service fees, international shipping, and sometimes customs duties. Use your spreadsheet to calculate and track the true total cost for every order, so you are never surprised by the final amount. For advanced cost-saving strategies that apply across all platforms, read our <Link to="/articles/shipping-optimization-guide" className="text-primary-600 underline hover:text-primary-700">shipping cost optimization guide</Link>.
              </p>
            </section>

            {/* CTA within article */}
            <div className="bg-secondary-100/60 rounded-2xl p-8 text-center my-12">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Ready to Start Your Superbuy Journey?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Download our free Superbuy spreadsheet template and place your first order with confidence.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  cssbuy sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 bg-primary-700 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  buy it now
                </a>
              </div>
            </div>

          </div>

          {/* Back to Home */}
          <div className="mt-16 pt-8 border-t border-background-200/60 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors underline"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to CSSSpreadsheet Homepage
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}